import { Component, Input, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Filter, FilterOption, ProductLabel, Variant } from '@infrab4a/connect';
import { ConfirmationService, MessageService } from 'primeng/api';
import {
  AutoCompleteCompleteEvent,
  AutoCompleteUnselectEvent
} from 'primeng/autocomplete';
import { ChipsAddEvent } from 'primeng/chips';
import { firstValueFrom, lastValueFrom, map } from 'rxjs';
import { UserControllerService } from 'src/app/admin-api';
import { ShopCategoryFilterService } from 'src/app/connect-api/api/shop/shop-category-filter.service';
import { ShopLoggingService } from 'src/app/connect-api/api/shop/shop-log-service';
import { ShopProductGroupService } from 'src/app/connect-api/api/shop/shop-product-group.service';
import { ShopProductService } from 'src/app/connect-api/api/shop/shop-product.service';
import { VariantDTO } from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { FormUtil } from 'src/app/utils/form.util';
import { slugify } from 'src/app/utils/slug.util';

@Component({
  selector: 'app-shop-product-variant-form',
  templateUrl: './shop-product-variant-form.component.html',
  styleUrl: './shop-product-variant-form.component.scss'
})
export class ShopProductVariantFormComponent implements OnInit {
  @Input()
  variant: Partial<VariantDTO>;
  @Input() permission = true;

  private confirmationService = inject(ConfirmationService);
  private productService = inject(ShopProductService);
  private messageService = inject(MessageService);
  private productGroupService = inject(ShopProductGroupService);
  private shopProductService = inject(ShopProductService);
  private filterService = inject(ShopCategoryFilterService);
  private shopLoggingService = inject(ShopLoggingService);
  private userService = inject(UserControllerService);

  chipRegex: any = /\r|\n|;|,/;
  form = new FormGroup({
    grade: new FormGroup({
      title: new FormControl<string>({ value: null, disabled: true }),
      value: new FormControl<string>({ value: null, disabled: true })
    }),
    sku: new FormControl<string>(null, [Validators.required]),
    EAN: new FormControl<string>({ value: null, disabled: true }, [
      Validators.required
    ]),
    weight: new FormControl<number>(0, [Validators.required]),
    costPrice: new FormControl<number>({ value: 0, disabled: true }, [
      Validators.required
    ]),
    stock: new FormGroup({
      quantity: new FormControl<number>({ value: 0, disabled: true }, [
        Validators.required
      ])
    }),
    label: new FormControl<ProductLabel>(null),
    price: new FormGroup({
      price: new FormControl<number>(0, [
        Validators.required,
        Validators.min(0.01)
      ]),
      fullPrice: new FormControl<number>(0, [
        Validators.required,
        Validators.min(0.01)
      ]),
      subscriberDiscountPercentage: new FormControl<number>(
        0,
        Validators.required
      ),
      fullPriceDiscountPercentage: new FormControl<number>(
        { value: 0, disabled: true },
        Validators.required
      ),
      subscriberPrice: new FormControl<number>(
        { value: 0, disabled: true },
        Validators.required
      )
    }),
    groupId: new FormControl<number>(null),
    validity: new FormControl<boolean>(null, [Validators.required]),
    published: new FormControl<boolean>(false),
    tagsCollection: new FormControl<string[]>(null),
    tagsProfile: new FormControl<string[]>(null),
    tagsSupply: new FormControl<string[]>(null),
    filters: new FormControl<FilterOption[]>(null),
    daysOfUse: new FormControl<string>(null),
    description: new FormGroup({
      description: new FormControl<string>(null, Validators.required),
      differentials: new FormControl<string>(null),
      whoMustUse: new FormControl<string>(null),
      howToUse: new FormControl<string>(null),
      brand: new FormControl<string>(null),
      ingredients: new FormControl<string>(null),
      purpose: new FormControl<string>(null)
    }),
    metadata: new FormGroup({
      title: new FormControl<string>(null, [Validators.minLength(1)]),
      description: new FormControl<string>(null, [Validators.minLength(1)])
    })
  });

  groups: { name: string; value: number }[] = [];
  validities = [
    { name: 'Sim', value: true },
    { name: 'Não', value: false }
  ];
  labels = [
    { name: 'Nenhum', value: 'null' },
    { name: 'Outlet', value: 'outlet' },
    { name: 'Promoção', value: 'on-sale' },
    { name: 'Últimas unidades', value: 'last-units' },
    { name: 'Glamstar', value: 'glamstar' }
  ];
  filterOptions: Filter[] = [];
  filterOptionsAvailable: FilterOption[] = [];

  async ngOnInit(): Promise<void> {
    await this.getVariantData();
    await this.getProductGroups();
    await this.findFiltersOptions();
    this.patchFormValue();
    if (!this.permission) this.form.disable();
    this.recalculatePrices();
  }

  async getVariantData() {
    this.variant = await this.shopProductService
      .getVariantById(this.variant.id)
      .then((variant) => {
        return {
          ...variant,
          grade: variant.grade.at(0)
        };
      });

    console.log(this.variant);
  }

  private patchFormValue() {
    this.form.patchValue({
      ...this.variant,
      groupId: +this.variant.groupId,
      daysOfUse: this.variant.daysOfUse?.toString(),
      filters: this.variant.filters
        ? this.variant.filters?.map((f) =>
            this.filterTags?.find((ft) => ft.id === f)
          )
        : []
    });
  }

  async getProductGroups() {
    const response = await this.productGroupService.getList();

    this.groups = response.map((group) => ({
      name: `Grupo: ${group.id} - ${group.name}`,
      value: +group.id
    }));
  }

  async findFiltersOptions(): Promise<void> {
    const filterOptions = await this.filterService.getFilterList();
    filterOptions.sort((f1, f2) => {
      if (
        FormUtil.semAcento(f1.description).toUpperCase() <
        FormUtil.semAcento(f2.description).toUpperCase()
      )
        return -1;
      if (
        FormUtil.semAcento(f1.description).toUpperCase() >
        FormUtil.semAcento(f2.description).toUpperCase()
      )
        return 1;
      return 0;
    });
    this.filterOptions = filterOptions;
  }

  recalculatePrices(): void {
    this.form.controls['price'].controls['subscriberPrice'].setValue(
      (1 - this.form.value.price.subscriberDiscountPercentage / 100) *
        (this.form.value.price.price || 0)
    );
    this.form.controls['price'].controls[
      'fullPriceDiscountPercentage'
    ].setValue(
      (100 *
        ((this.form.value.price.fullPrice || 0) -
          (this.form.getRawValue().price.subscriberPrice || 0))) /
        (this.form.value.price.fullPrice || 1)
    );
  }

  async submit(): Promise<void> {
    if (this.form.valid) {
      try {
        LoaderService.showLoader();
        const variant = FormUtil.getTouchedAndDirtyOnly(this.form);
        if (Object.keys(variant).length) {
          this.variant = await this.productService.updateVariant(
            {
              ...variant,
              filters: this.form.value.filters?.map((f) => f.id) || []
            },
            this.variant.id
          );

          await this.createLog({
            ...variant,
            id: this.variant.id,
            filters: this.form.value.filters?.map((f) => f.id) || []
          });

          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Variante atualizada com sucesso.'
          });
        }
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      } finally {
        LoaderService.showLoader(false);
      }
    }
  }

  addCollectionTag(event?: ChipsAddEvent) {
    if (event?.value) {
      const index = this.form.value.tagsCollection.findIndex(
        (t) => t === slugify((event.value as string).toLowerCase())
      );
      if (index >= 0 && index < this.form.value.tagsCollection.length - 1) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Atenção',
          detail: `Tag já vinculada '${this.form.value.tagsCollection[index]}'`
        });
        this.form.controls['tagsCollection'].setValue(
          this.form.value.tagsCollection.slice(
            0,
            this.form.value.tagsCollection.length - 1
          )
        );
      }
    }
    this.form.controls['tagsCollection'].setValue(
      this.form.value.tagsCollection.map((t) => slugify(t.toLowerCase()))
    );
  }

  addProfileTag(event?: ChipsAddEvent) {
    if (event?.value) {
      const index = this.form.value.tagsProfile.findIndex(
        (t) => t === slugify((event.value as string).toLowerCase())
      );
      if (index >= 0 && index < this.form.value.tagsProfile.length - 1) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Atenção',
          detail: `Tag já vinculada '${this.form.value.tagsProfile[index]}'`
        });
        this.form.controls['tagsProfile'].setValue(
          this.form.value.tagsProfile.slice(
            0,
            this.form.value.tagsProfile.length - 1
          )
        );
      }
    }
    this.form.controls['tagsProfile'].setValue(
      this.form.value.tagsProfile.map((t) => slugify(t.toLowerCase()))
    );
  }

  addSupplyTag(event?: ChipsAddEvent) {
    if (event?.value) {
      const index = this.form.value.tagsSupply.findIndex(
        (t) => t === slugify((event.value as string).toLowerCase())
      );
      if (index >= 0 && index < this.form.value.tagsSupply.length - 1) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Atenção',
          detail: `Tag já vinculada '${this.form.value.tagsSupply[index]}'`
        });
        this.form.controls['tagsSupply'].setValue(
          this.form.value.tagsSupply.slice(
            0,
            this.form.value.tagsSupply.length - 1
          )
        );
      }
    }
    this.form.controls['tagsSupply'].setValue(
      this.form.value.tagsSupply.map((t) => slugify(t.toLowerCase()))
    );
  }

  searchFilters(event: AutoCompleteCompleteEvent): void {
    this.filterOptionsAvailable =
      this.filterTags?.filter(
        (f) =>
          !this.form.value.filters?.some((cf) => cf.id === f.id) &&
          f.id.includes(slugify(FormUtil.semAcento(event.query.toLowerCase())))
      ) || [];
  }

  removeFilter(event: AutoCompleteUnselectEvent): void {
    if (event.value) {
      this.form.controls['filters'].setValue(
        this.form.value.filters.filter((f) => f !== event.value)
      );
    }
  }

  get filterTags(): FilterOption[] {
    return this.filterOptions?.reduce(
      (options, f) => (options = options.concat(f.options)),
      []
    );
  }

  confirmDeleteImage(image: string, $event: any) {
    this.confirmationService.confirm({
      target: $event.target,
      message: 'Deseja excluir a imagem?',
      header: 'Atenção',
      icon: 'pi pi-exclamation-triangle danger',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-danger',
      accept: async () => {
        LoaderService.showLoader();
        try {
          await firstValueFrom(
            this.shopProductService.removeImage(this.variant.id, image)
          );
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Imagem removida com sucesso'
          });
        } catch (error) {
          AppDialogService.showErrorDialog(error);
        } finally {
          LoaderService.showLoader(false);
        }
      }
    });
  }

  async createLog(document: Partial<Variant>) {
    const user = await lastValueFrom(
      this.userService.getInfo().pipe(map((data) => data.result))
    );

    return this.shopLoggingService.createLog('products', document, {
      id: user.userId.toString(),
      name: user.displayName,
      email: user.username
    });
  }
}
